import { useQuery } from '@tanstack/react-query'
import { multicall, readContract } from '@wagmi/core'
import { formatUnits, Address } from 'viem'
import { useAccount } from 'wagmi'

import { erc20 } from 'abi/ERC20'
import { erc721 } from 'abi/ERC721'

const ADDRESS_ZERO = '0x0000000000000000000000000000000000000000'
const ETHERSCAN_API = 'https://api.etherscan.io/v2/api'

// Approval event topic for ERC721/ERC20
const APPROVAL_TOPIC = '0x8c5be1e5ebec7d5bd14f71427d1e84f3dd0314c0f7b2291e5b200ac8c7c3b925'
// ApprovalForAll event topic
const APPROVAL_FOR_ALL_TOPIC = '0x17307eab39ab6107e8899845ad3d59bd9653f200f220920489ca2b5937696c31'
// Transfer event topic
const TRANSFER_TOPIC = '0xddf252ad1be2c89b69c2b068fc378daa952ba7f163c4a11628f55a4df523b3ef'

interface EtherscanLog {
  address: string
  topics: string[]
  data: string
  blockNumber: string
  transactionHash: string
  transactionIndex: string
  blockHash: string
  logIndex: string
  removed: boolean
}

const fetchLogsFromEtherscan = async (
  topic0: string,
  topic1: string | null,
  topic2: string | null
): Promise<EtherscanLog[]> => {
  const apiKey = process.env.NEXT_PUBLIC_ETHERSCAN_API_KEY || ''
  let url = `${ETHERSCAN_API}?chainid=1&module=logs&action=getLogs&fromBlock=17000000&toBlock=latest&topic0=${topic0}&apikey=${apiKey}`

  if (topic1) {
    url += `&topic1=${topic1}&topic0_1_opr=and`
  }
  if (topic2) {
    url += `&topic2=${topic2}&topic0_2_opr=and`
  }

  const response = await fetch(url)
  const data = await response.json()

  if (data.status !== '1' || !data.result) {
    console.log('ETHERSCAN LOGS ERROR', data)
    return []
  }

  return data.result
}

const logSorterChronological = (a: any, b: any) => {
  if (a.blockNumber < b.blockNumber) return -1

  if (a.blockNumber > b.blockNumber) return 1

  if (a.transactionIndex < b.transactionIndex) return -1

  if (a.transactionIndex > b.transactionIndex) return 1

  if (a.logIndex < b.logIndex) return -1

  if (a.logIndex > b.logIndex) return 1

  return 0
}

const filteredAllowancesBySpender = (allowances: any) => {
  return allowances.filter(
    (value: any, index: any, self: any) =>
      index === self.findIndex((t: any) => t.topics[2] === value.topics[2])
  )
}

const getAddressFromTopic = (topic: string): Address => {
  return ('0x' + topic.substring(26)) as Address
}

export function useEventLogs() {
  const { address } = useAccount()

  const queryFn = async () => {
    // Pad address to 32 bytes for topic filtering
    const paddedAddress = address ? '0x000000000000000000000000' + address.slice(2).toLowerCase() : null

    const [approvalEvents, approvalForAllEvents, transferToEvents] = await Promise.all([
      fetchLogsFromEtherscan(APPROVAL_TOPIC, paddedAddress, null),
      fetchLogsFromEtherscan(APPROVAL_FOR_ALL_TOPIC, paddedAddress, null),
      fetchLogsFromEtherscan(TRANSFER_TOPIC, paddedAddress, null),
    ])

    const allEvents = [...approvalEvents, ...approvalForAllEvents, ...transferToEvents]
    // console.log('all events', allEvents)

    const tokenContracts = allEvents
      .filter((event, i) => i === allEvents.findIndex((other) => event.address === other.address))
      .map(async (event) => {
        const approvalsForAll = approvalForAllEvents.filter((log) => log.address === event.address)
        const approvals = approvalEvents.filter((log) => log.address === event.address)

        //ERC721 if topic length 4
        if (event.topics.length === 4) {
          const erc721Config = {
            abi: erc721,
            address: event.address as Address,
          }

          const unlimitedAllowances = filteredAllowancesBySpender(
            approvalsForAll.sort(logSorterChronological)
          ).map(async (appEvent: any) => {
            const spender = getAddressFromTopic(appEvent.topics[2])

            // const isApprovedForAll = appEvent.data !== '0x'

            // if (isApprovedForAll) {
            // const { timestamp } = await provider.getBlock(appEvent.blockNumber)
            return {
              spender, // to adrs
              spenderContractName: '',
              timestamp: '',
              transactionHash: appEvent.transactionHash,
            }
            // }
          })

          const limitedAllowances = filteredAllowancesBySpender(
            approvals.sort(logSorterChronological)
          ).map(async (appEvent: any) => {
            const tokenId = BigInt(appEvent.topics[3]).toString()

            const spender = getAddressFromTopic(appEvent.topics[2])
            // const { timestamp } = await provider.getBlock(appEvent.blockNumber)
            if (spender === ADDRESS_ZERO) return

            try {
              const [owner, spender] = (await multicall({
                allowFailure: false,
                contracts: [
                  { ...erc721Config, functionName: 'ownerOf', args: [BigInt(tokenId)] },
                  {
                    ...erc721Config,
                    functionName: 'getApproved',
                    args: [BigInt(tokenId)],
                  },
                ],
              })) as string[]

              console.log(' limited allowance owner spender', { owner, spender })
              const expectedOwner = getAddressFromTopic(appEvent.topics[1])
              if (owner !== expectedOwner) return

              return {
                spender,
                spenderContractName: '',
                owner,
                timestamp: '',
                tokenId,
                transactionHash: appEvent.transactionHash,
              }
            } catch (err) {
              console.log('ERROR: Limited allowance get ownderOf and getApproved', err)
            }
          })

          const allAllowances = [...unlimitedAllowances, ...limitedAllowances]

          const filteredAllowances = await Promise.all(allAllowances)
          console.log({ filteredAllowances })

          if (allAllowances.length) {
            try {
              const [name, value] = (await multicall({
                allowFailure: false,

                contracts: [
                  { ...erc721Config, functionName: 'name' },
                  {
                    ...erc721Config,
                    functionName: 'balanceOf',
                    args: [address as Address],
                  },
                ],
              })) as string[]

              const openSeaFloor = 0

              return {
                tokenType: 'erc721',
                contractAddress: event.address,
                contractName: name,
                allowances: filteredAllowances.flat().filter((allowance) => allowance?.spender),
                balance: parseFloat(value),
                floorPrice: openSeaFloor,
                transactionHash: event.transactionHash,
              }
            } catch (error) {
              console.log('erc721 error', error)
            }
          }
        }

        //ERC20 if topic length 3
        if (event.topics.length === 3) {
          const erc20Config = {
            abi: erc20,
            address: event.address as Address,
          }

          const allowances = filteredAllowancesBySpender(
            approvals.sort(logSorterChronological)
          ).map(async (appEvent: any) => {
            // const { timestamp } = await provider.getBlock(appEvent.blockNumber)
            const spender = getAddressFromTopic(appEvent.topics[2])

            try {
              const amount = await readContract({
                ...erc20Config,
                functionName: 'allowance',
                args: [address as Address, spender],
              })
              if (Number(amount) === 0) return

              return {
                spender,
                spenderContractName: '',
                amount,
                timestamp: '',
                transactionHash: appEvent.transactionHash,
              }
            } catch (err) {
              console.log('ERROR: ERC20 get allowance', err)
            }
          })

          if (allowances.length) {
            try {
              const [name, value, decimals, symbol, totalSupply] = (await multicall({
                allowFailure: false,
                contracts: [
                  { ...erc20Config, functionName: 'name' },
                  {
                    ...erc20Config,
                    functionName: 'balanceOf',
                    args: [address as Address],
                  },
                  { ...erc20Config, functionName: 'decimals' },
                  { ...erc20Config, functionName: 'symbol' },
                  { ...erc20Config, functionName: 'totalSupply' },
                ],
              })) as string[]
              const filteredAllowances = await Promise.all(allowances)

              return {
                tokenType: 'erc20',
                contractName: name,
                symbol,
                allowances: filteredAllowances.flat().filter((allowance) => allowance.spender),
                contractAddress: event.address,
                balance: Number(formatUnits(BigInt(value), Number(decimals))),
                decimals,
                totalSupply,
                transactionHash: event.transactionHash,
              }
            } catch (error) {
              console.log('erc20 error', error)
            }
          }
        }
      })

    return await Promise.all(tokenContracts)
  }

  return useQuery({
    enabled: Boolean(address),
    queryKey: [
      {
        entity: 'getEvents',
        address,
      },
    ],
    queryFn,
    refetchOnWindowFocus: false,
    staleTime: 60 * 1000,
    cacheTime: Infinity,
    retry: false,
  })
}
